
import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/HttpClient';
import { ThemeProvider } from '@mui/material/styles';
import { CircularProgress } from '@mui/material';
import ShortTermVacancy from './ShortTermVacancy';
import LongTermVacancy from './LongTermVacancy';
import theme from './theme';
import { Button } from '../ui/button';
import { Settings, Upload, MapPin, Utensils } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import * as hostService from '../../api/services/hostService';
import { getImageUrl } from '../../utils/imageUrl';
import { ICreatePropertyRequest } from '../../app/model/hostModel';
import { useUserContext } from '../../app/protectedRoute/UserContext';
import { MultiSelect } from '../ui/multi-select';
import LocationPicker from '../common/LocationPicker';
import { Textarea } from '../ui/textarea';
import { cn } from "../../lib/utils";

interface PropertyDashboardProps {
  propertyId: string;
}

interface Property {
  id: string;
  name: string;
  property_type: 'hotel' | 'pg';
  location: {
    street_address: string;
    city: string;
    locality: string;
    latitude: number;
    longitude: number;
  };
  description: string;
  gender_type: string;
  phone: string;
  email: string;
  food_available: boolean;
  facilities: string[];
  highlights: string[];
  property_rules: string[];
  images: { image: string }[];
}

const parseJsonList = (value: any): string[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.length > 0) {
    try { const p = JSON.parse(value); if (Array.isArray(p)) return p; } catch {}
  }
  return [];
};

const PropertyDashboard: React.FC<PropertyDashboardProps> = ({ propertyId }) => {
  const [activeTab, setActiveTab] = useState('list-earn');
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditPropertyDialog, setShowEditPropertyDialog] = useState(false);
  const { state, dispatch } = useUserContext();
  const { organization_id } = state;
  const [propertyForm, setPropertyForm] = useState<ICreatePropertyRequest>({
    host: 0,
    name: "",
    location: {
      street_address: "",
      city: "",
      locality: "",
      latitude: 0,
      longitude: 0,
    },
    property_type: "",
    property_gender_type: "",
    description: "",
    food_available: false,
    facilities: [],
    highlights: [],
    property_rules: [],
    images: [],
  });
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<{ id: number; image: string }[]>([]);


  useEffect(() => {
    const fetchPropertyDetails = async () => {
      setLoading(true);
      try {
        const response = await hostService.getPropertyDetails(propertyId);
        setProperty(response);
        setExistingImages(response.images || []);
        setPropertyForm({
          host: organization_id ? parseInt(organization_id) : 0,
          name: response.name,
          location: {
            street_address: response.location?.street_address || '',
            city: (response.location?.city || '').toLowerCase(),
            locality: response.location?.locality || '',
            latitude: response.location?.latitude || 0,
            longitude: response.location?.longitude || 0,
          },
          property_type: response.property_type || '',
          property_gender_type: response.property_gender_type || '',
          description: response.description || '',
          food_available: response.food_available ?? false,
          facilities: parseJsonList(response.facilities),
          highlights: parseJsonList(response.highlights),
          property_rules: parseJsonList(response.property_rules),
          images: [],
        });
      } catch (error) {
        console.error('Failed to fetch property details:', error);
        setProperty(null);
      } finally {
        setLoading(false);
      }
    };

    if (organization_id) {
      dispatch({ type: 'SET_PROPERTY_ID', payload: propertyId });
      fetchPropertyDetails();
    }
  }, [propertyId, organization_id]);

  const handleUpdateProperty = async () => {
    try {
      const formData = new FormData();
      formData.append("name", propertyForm.name);
      formData.append("description", propertyForm.description);
      formData.append("property_type", propertyForm.property_type);
      formData.append("property_gender_type", propertyForm.property_gender_type);
      formData.append("food_available", propertyForm.food_available.toString());
      formData.append("host", propertyForm.host.toString());
      formData.append("location", JSON.stringify(propertyForm.location));
      formData.append("facilities", JSON.stringify(propertyForm.facilities));
      formData.append("highlights", JSON.stringify(propertyForm.highlights));
      formData.append("property_rules", JSON.stringify(propertyForm.property_rules));
      
      imageFiles.forEach((file) => {
          formData.append("image", file);
      });

      await hostService.updateProperty(propertyId, formData as any);
      setShowEditPropertyDialog(false);
      // Refresh property details
      const response = await hostService.getPropertyDetails(propertyId);
      setProperty(response);
    } catch (error) {
      console.error('Failed to update property:', error);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const filesArray = Array.from(event.target.files);
      setImageFiles((prev) => [...prev, ...filesArray]);
    }
  };

  const removeImage = (index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const facilityOptions = [
    { value: "wifi", label: "WiFi" },
    { value: "ac", label: "AC" },
    { value: "parking", label: "Parking" },
    { value: "laundry", label: "Laundry" },
    { value: "gym", label: "Gym" },
    { value: "security", label: "24/7 Security" },
];

const highlightsOptions = [
    { value: "metro-nearby", label: "Metro Nearby" },
    { value: "market-nearby", label: "Market Nearby" },
    { value: "hospital-nearby", label: "Hospital Nearby" },
    { value: "college-nearby", label: "College Nearby" },
    { value: "it-park", label: "IT Park Nearby" },
];

const propertyRulesOptions = [
    { value: "no-smoking", label: "No Smoking" },
    { value: "no-drinking", label: "No Drinking" },
    { value: "no-pets", label: "No Pets" },
    { value: "guest-timing", label: "Guest Timing" },
    { value: "gate-closing", label: "Gate Closing Time" },
];


  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <CircularProgress />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="p-3">
        <p className="text-red-500 text-lg">Property not found.</p>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div className="p-2 md:p-3 bg-gray-100 min-h-screen">
        <div className="bg-white rounded-xl shadow-lg p-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              {property.name} - <span className="text-primary">Vacancy Dashboard</span>
            </h1>
            <Button
              variant="outline"
              onClick={() => setShowEditPropertyDialog(true)}
              className="gap-2"
            >
              <Settings className="h-4 w-4" />
              Update Property
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 my-6">
            <button
              onClick={() => setActiveTab("list-earn")}
              className={cn(
                "flex-1 py-3 px-6 rounded-lg font-semibold text-center transition-all duration-300",
                activeTab === "list-earn"
                  ? "bg-primary text-primary-foreground shadow-lg"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              Earn from Vacancies
            </button>
            {property.property_type === 'pg' && (
            <button
              onClick={() => setActiveTab("tenant")}
              className={cn(
                "flex-1 py-3 px-6 rounded-lg font-semibold text-center transition-all duration-300",
                activeTab === "tenant"
                  ? "bg-primary text-primary-foreground shadow-lg"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              Tenant Management
            </button>
            )}
          </div>
          <div className="pt-3">
            {activeTab === 'list-earn' && <ShortTermVacancy propertyId={propertyId} />}
            {activeTab === 'tenant' && property.property_type === 'pg' && <LongTermVacancy propertyId={propertyId} propertyName={property.name} />}
          </div>
        </div>
      </div>
      <Dialog open={showEditPropertyDialog} onOpenChange={setShowEditPropertyDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Settings className="h-5 w-5 text-primary" />
              Edit Property Details
            </DialogTitle>
            <DialogDescription>
              Update your property information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
          <div className="space-y-2">
              <Label htmlFor="name">Property Name *</Label>
              <Input 
                  id="name"
                  placeholder="Enter property name"
                  value={propertyForm.name}
                  onChange={(e) => setPropertyForm({ ...propertyForm, name: e.target.value })}
                  className="bg-background"
              />
          </div>

          <div className="space-y-2">
              <Label htmlFor="street_address" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  Street Address *
              </Label>
              <Input 
                  id="street_address"
                  placeholder="Enter street address"
                  value={propertyForm.location.street_address}
                  onChange={(e) => setPropertyForm({ ...propertyForm, location: { ...propertyForm.location, street_address: e.target.value } })}
                  className="bg-background"
              />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Select onValueChange={(v) => setPropertyForm({ ...propertyForm, location: { ...propertyForm.location, city: v } })} value={propertyForm.location.city}>
                      <SelectTrigger className="bg-background">
                          <SelectValue placeholder="Select City" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border z-50">
                          <SelectItem value="bangalore">Bangalore</SelectItem>
                          <SelectItem value="mumbai">Mumbai</SelectItem>
                          <SelectItem value="delhi">Delhi</SelectItem>
                          <SelectItem value="hyderabad">Hyderabad</SelectItem>
                          <SelectItem value="chennai">Chennai</SelectItem>
                          <SelectItem value="pune">Pune</SelectItem>
                      </SelectContent>
                  </Select>
              </div>
              <div className="space-y-2">
                  <Label htmlFor="locality">Locality *</Label>
                  <Input 
                      id="locality"
                      placeholder="Enter locality"
                      value={propertyForm.location.locality}
                      onChange={(e) => setPropertyForm({ ...propertyForm, location: { ...propertyForm.location, locality: e.target.value } })}
                      className="bg-background"
                  />
              </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                  <Label>Property Type *</Label>
                  <Select onValueChange={(v) => setPropertyForm({ ...propertyForm, property_type: v })} value={propertyForm.property_type}>
                      <SelectTrigger className="bg-background">
                          <SelectValue placeholder="Select Type" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border z-50">
                          <SelectItem value="pg">PG</SelectItem>
                          <SelectItem value="hostel">Hostel</SelectItem>
                          <SelectItem value="coliving">Co-Living</SelectItem>
                          <SelectItem value="apartment">Apartment</SelectItem>
                      </SelectContent>
                  </Select>
              </div>
              <div className="space-y-2">
                  <Label>Property Gender Type *</Label>
                  <Select onValueChange={(v) => setPropertyForm({ ...propertyForm, property_gender_type: v })} value={propertyForm.property_gender_type}>
                      <SelectTrigger className="bg-background">
                          <SelectValue placeholder="Select Gender" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border z-50">
                          <SelectItem value="male">Male Only</SelectItem>
                          <SelectItem value="female">Female Only</SelectItem>
                          <SelectItem value="unisex">Co-ed</SelectItem>
                      </SelectContent>
                  </Select>
              </div>
          </div>

          <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea 
                  id="description"
                  placeholder="Describe your property..."
                  value={propertyForm.description}
                  onChange={(e) => setPropertyForm({ ...propertyForm, description: e.target.value })}
                  className="bg-background min-h-[80px]"
              />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                  <Label>Facilities</Label>
                  <MultiSelect
                      options={facilityOptions}
                      value={propertyForm.facilities}
                      onChange={(value) => setPropertyForm({ ...propertyForm, facilities: value })}
                      placeholder="Select Facilities"
                  />
              </div>
              <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                      <Utensils className="h-4 w-4 text-muted-foreground" />
                      Food Available
                  </Label>
                  <Select onValueChange={(value) => setPropertyForm({ ...propertyForm, food_available: value === "true" })} value={propertyForm.food_available.toString()}>
                      <SelectTrigger className="bg-background">
                          <SelectValue placeholder="Food Available" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border z-50">
                          <SelectItem value="true">Yes</SelectItem>
                          <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                  </Select>
              </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                  <Label>Highlights</Label>
                  <MultiSelect
                      options={highlightsOptions}
                      value={propertyForm.highlights}
                      onChange={(value) => setPropertyForm({ ...propertyForm, highlights: value })}
                      placeholder="Select Highlights"
                  />
              </div>
              <div className="space-y-2">
                  <Label>Property Rules</Label>
                  <MultiSelect
                      options={propertyRulesOptions}
                      value={propertyForm.property_rules}
                      onChange={(value) => setPropertyForm({ ...propertyForm, property_rules: value })}
                      placeholder="Select Rules"
                  />
              </div>
          </div>

          <div className="space-y-2">
              <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  Property Location
              </Label>
              <div className="border border-border rounded-lg bg-muted/30 h-60 flex items-center justify-center">
                  <LocationPicker
                      initialLat={propertyForm.location.latitude}
                      initialLng={propertyForm.location.longitude}
                      onLocationChange={(lat, lng) => {
                          setPropertyForm({ ...propertyForm, location: { ...propertyForm.location, latitude: lat, longitude: lng } });
                      }}
                  />
              </div>
          </div>

          <div className="space-y-2">
              <Label>Property Images</Label>
              <Label htmlFor="image-upload" className="w-full border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center gap-2 hover:border-primary/50 transition-colors cursor-pointer">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Click to upload images</span>
              </Label>
              <Input
                  type="file"
                  multiple
                  className="hidden"
                  id="image-upload"
                  accept="image/*"
                  onChange={handleImageUpload}
              />
              <div className="flex flex-wrap gap-4 mt-4">
                  {existingImages.map((img) => (
                      <div key={img.id} className="relative">
                          <img
                              src={getImageUrl(img.image)}
                              alt="Existing"
                              className="w-32 h-32 object-cover rounded-lg border border-border"
                          />
                          <Button
                              variant="destructive"
                              onClick={async () => {
                                  try {
                                      await hostService.deletePropertyImage(img.id);
                                      setExistingImages(prev => prev.filter(i => i.id !== img.id));
                                  } catch (e) { console.error('Failed to delete image', e); }
                              }}
                              className="absolute top-1 right-1 h-6 w-6 p-0 text-xs"
                              size="sm"
                          >
                              ×
                          </Button>
                      </div>
                  ))}
                  {imageFiles.map((file, index) => (
                      <div key={index} className="relative">
                          <img
                              src={URL.createObjectURL(file)}
                              alt={`Preview ${index}`}
                              className="w-32 h-32 object-cover rounded-lg border border-dashed border-primary/50"
                          />
                          <Button
                              variant="destructive"
                              onClick={() => removeImage(index)}
                              className="absolute top-1 right-1 h-6 w-6 p-0 text-xs"
                              size="sm"
                          >
                              ×
                          </Button>
                      </div>
                  ))}
              </div>
          </div>
      </div>


          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button variant="outline" onClick={() => setShowEditPropertyDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateProperty}>
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
};

export default PropertyDashboard;
